import { useEffect, useRef, useState } from 'react';
import { Card, Button, Input } from '../../ui';
import { useAdminApi } from '../../hooks/useAdminApi';

interface ContactRequest {
  id: number;
  name: string;
  email: string;
  organization: string | null;
  role: string | null;
  inquiry_type: string | null;
  message: string | null;
  status: string;
  assigned_to: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string | null;
}

const STATUS_COLORS: Record<string, string> = {
  new: 'bg-blue-100 text-blue-800',
  responded: 'bg-green-100 text-green-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  closed: 'bg-gray-100 text-gray-800',
};

export default function AdminContacts() {
  const [contacts, setContacts] = useState<ContactRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editNotes, setEditNotes] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const api = useAdminApi();

  const fetchContacts = () => {
    api.get('/api/admin/contacts')
      .then((res) => setContacts(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const initRef = useRef(false);
  useEffect(() => { if (!initRef.current) { initRef.current = true; fetchContacts(); } }, [api]);

  const handleUpdate = async (id: number) => {
    try {
      await api.put(`/api/admin/contacts/${id}`, {
        status: editStatus || undefined,
        notes: editNotes || undefined,
      });
      setEditingId(null);
      fetchContacts();
    } catch (err) {
      console.error('Update failed:', err);
    }
  };

  const filtered = contacts.filter((c) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return c.name.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      (c.organization?.toLowerCase().includes(q) ?? false) ||
      (c.inquiry_type?.toLowerCase().includes(q) ?? false);
  });

  if (loading) return <div className="py-12 text-center text-muted-foreground">Loading contacts...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Contact Requests ({contacts.length})</h2>
        <Input
          placeholder="Search contacts..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-xs"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-3 py-2 text-left font-medium">Name</th>
              <th className="px-3 py-2 text-left font-medium">Email</th>
              <th className="px-3 py-2 text-left font-medium">Organization</th>
              <th className="px-3 py-2 text-left font-medium">Type</th>
              <th className="px-3 py-2 text-left font-medium">Status</th>
              <th className="px-3 py-2 text-left font-medium">Date</th>
              <th className="px-3 py-2 text-left font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} className="border-b hover:bg-muted/30">
                <td className="px-3 py-2 font-medium">{c.name}</td>
                <td className="px-3 py-2">
                  <a href={`mailto:${c.email}`} className="text-primary hover:underline">{c.email}</a>
                </td>
                <td className="px-3 py-2 text-muted-foreground">{c.organization || '—'}</td>
                <td className="px-3 py-2">
                  <span className="capitalize">{c.inquiry_type?.replace('_', ' ') || '—'}</span>
                </td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[c.status] || 'bg-gray-100'}`}>
                    {c.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                  {new Date(c.created_at).toLocaleDateString()}
                </td>
                <td className="px-3 py-2">
                  {editingId === c.id ? (
                    <div className="flex items-center gap-2">
                      <select
                        value={editStatus}
                        onChange={(e) => setEditStatus(e.target.value)}
                        className="text-xs border rounded px-1 py-0.5"
                      >
                        <option value="">Status...</option>
                        <option value="new">New</option>
                        <option value="in_progress">In Progress</option>
                        <option value="responded">Responded</option>
                        <option value="closed">Closed</option>
                      </select>
                      <Input
                        placeholder="Notes..."
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        className="text-xs h-7 w-32"
                      />
                      <Button size="sm" onClick={() => handleUpdate(c.id)}>Save</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditingId(c.id);
                        setEditStatus(c.status);
                        setEditNotes(c.notes || '');
                      }}
                    >
                      Edit
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            {filter ? 'No contacts match your search' : 'No contact requests yet'}
          </div>
        )}
      </div>

      {/* Expandable detail for messages */}
      {filtered.filter(c => c.message).length > 0 && (
        <Card className="p-4">
          <h3 className="text-sm font-medium mb-3">Recent Messages</h3>
          <div className="space-y-3">
            {filtered.filter(c => c.message).slice(0, 5).map(c => (
              <div key={c.id} className="border-l-2 border-primary/30 pl-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{c.name}</span>
                  <span>({c.inquiry_type})</span>
                  <span>{new Date(c.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-sm mt-1">{c.message}</p>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
